import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import { Icon, variants } from '@uag/react-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType, type SolverCostWeights } from 'api/models';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';
type WeightRow = { key: keyof SolverCostWeights; label: string };

interface SolverCostWeightsMenuProps {
    settingType: SettingModelSettingType;
}

export const SolverCostWeightsMenu = ({ settingType }: SolverCostWeightsMenuProps) => {
    const { t } = useTranslation();
    const { settings, setWeights } = usePlacementStore(
        useShallow((state) => ({
            settings:
                settingType === SettingModelSettingType.Anchors
                    ? undefined
                    : state.automatedPlacementSettingsByType[settingType],
            setWeights: state.setAutomatedSolverCostWeightsByType
        }))
    );

    const weightRows = useMemo((): WeightRow[] => {
        const common = [
            { key: 'instanceCount', label: t('labelSolverWeightInstanceCount') },
            { key: 'verticalOverbuild', label: t('labelSolverWeightVerticalOverbuild') },
            { key: 'materialScarcity', label: t('labelSolverWeightMaterialScarcity') },
            { key: 'groupPreferenceBonus', label: t('labelSolverWeightGroupPreferenceBonus') },
            { key: 'lockedPanelChangePenalty', label: t('labelSolverWeightLockedPanelChangePenalty') },
            { key: 'price', label: t('labelSolverWeightPrice') },
            { key: 'co2', label: t('labelSolverWeightCo2') },
            { key: 'ppm', label: t('labelSolverWeightPpm') }
        ] satisfies WeightRow[];

        return settingType === SettingModelSettingType.Panels
            ? [
                  ...common.slice(0, 2),
                  { key: 'gapWidth', label: t('labelSolverWeightGapWidth') },
                  { key: 'gapPositionDeviation', label: t('labelSolverWeightGapPositionDeviation') },
                  { key: 'fillerUsage', label: t('labelSolverWeightFillerUsage') },
                  { key: 'fillerWidthPenalty', label: t('labelSolverWeightFillerWidthPenalty') },
                  ...common.slice(2)
              ]
            : common;
    }, [settingType, t]);

    if (!IS_DEV_TOOLS || !settings) {
        return null;
    }

    return (
        <Menu position="bottom-end" shadow="md" withArrow>
            <Menu.Target>
                <Tooltip label={t('labelSolverCostWeights')} position="bottom" variant={variants.tooltip.bottom}>
                    <ActionIcon
                        aria-label={t('labelSolverCostWeights')}
                        className="bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto action-icon-small"
                    >
                        <Icon color="text-text-accent" icon="functions" size="sm" />
                    </ActionIcon>
                </Tooltip>
            </Menu.Target>
            <Menu.Dropdown className="max-h-[min(32rem,calc(100vh-2rem))] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto">
                <Menu.Label>{t('labelSolverCostWeights')}</Menu.Label>
                <Menu.Divider />
                <div className="px-2 pb-2 text-xs text-text-gray-dark">{t('labelSolverCostWeightsHint')}</div>
                <div className="grid grid-cols-[minmax(0,1fr)_5rem] items-center gap-x-2 gap-y-1 px-2 text-xs">
                    {weightRows.map((row) => (
                        <div key={row.key} className="contents">
                            <label htmlFor={`automated-solver-weight-${settingType}-${row.key}`}>{row.label}</label>
                            <input
                                className="h-7 rounded-sm border border-border-light px-2 text-right"
                                id={`automated-solver-weight-${settingType}-${row.key}`}
                                step={0.1}
                                type="number"
                                value={settings.solverCostWeights[row.key]}
                                onChange={(event) => {
                                    const parsed = Number.parseFloat(event.target.value);
                                    setWeights(settingType, { [row.key]: Number.isFinite(parsed) ? parsed : 0 });
                                }}
                            />
                        </div>
                    ))}
                </div>
            </Menu.Dropdown>
        </Menu>
    );
};
